"use client";

import { BlogCardReadMoreArrow } from "@/components/molecules/BlogCardReadMoreArrow";
import { LATEST_BLOGS_1_WIDGET_DEFAULTS } from "@/constants/admin/fields/latestBlogs/latest_blogs_1";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** Match BlogList1 card date styling defaults. */
const BLOG_CARD_DATE_TEXT = "#373737";
const BLOG_CARD_DATE_ICON = "#373737";

interface LatestBlogMeta {
  id?: string;
  meta_cover_image?: string;
  meta_cover_alt?: string;
  meta_date_display?: string;
  meta_title?: string;
  meta_description?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean | number | string;
}

const metaVisible = (item: LatestBlogMeta) =>
  item.meta_status === true ||
  item.meta_status === 1 ||
  item.meta_status === "1";

export default function LatestBlogs1({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const raw = (data as any)?.data ?? data;
  const sectionData = { ...LATEST_BLOGS_1_WIDGET_DEFAULTS, ...raw };

  /** Leftmost visible card index; arrows move by one card. */
  const [startIndex, setStartIndex] = useState(0);
  const isMobileHook = useIsMobile(768);
  /** Same breakpoint as ExploreCourses1 / ProgramCards1 (`null` → desktop until measured). */
  const isMobileLayout = isMobileHook === true;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "latest_blogs_section");
  //   }
  // }, []);

  const {
    title,
    title_text_color,
    section_background_color,
    read_more_label,
    read_more_text_color,
    nav_button_active_bg,
    nav_button_inactive_bg,
    nav_icon_color,
    metadata,
  } = sectionData || {};

  const posts = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter(metaVisible)
      .sort(
        (a: LatestBlogMeta, b: LatestBlogMeta) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
      );
  }, [metadata]);

  const visibleSlots =
    isMobileLayout ? 1 : Math.min(3, posts.length);
  const maxStart = Math.max(0, posts.length - visibleSlots);

  useEffect(() => {
    setStartIndex((i) => Math.min(i, maxStart));
  }, [maxStart, posts.length, visibleSlots]);

  const visiblePosts = posts.slice(startIndex, startIndex + visibleSlots);

  const goPrev = useCallback(() => {
    setStartIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setStartIndex((i) => Math.min(maxStart, i + 1));
  }, [maxStart]);

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!sectionData || !isActive || posts.length === 0) {
    return null;
  }

  const showNav = posts.length > visibleSlots;
  const canPrev = startIndex > 0;
  const canNext = startIndex < maxStart;

  const prevBg = canPrev ? nav_button_active_bg : nav_button_inactive_bg;
  const nextBg = canNext ? nav_button_active_bg : nav_button_inactive_bg;

  const readMoreRow = (href: string) => {
    const trimmed = href.trim();
    const label = read_more_label || "Read More";
    const color = read_more_text_color || "#60050e";
    const arrowCls =
      "size-[12px] tab:size-[13px] md:size-[14px] lg:size-4 xl:size-[18px]";
    const rowCls = cn(
      "inline-flex items-center transition-opacity hover:opacity-90",
      "gap-[6px] tab:gap-2 md:gap-2 lg:gap-2.5 xl:gap-3",
    );
    const labelCls = cn(
      "text-center font-semibold leading-tight tracking-0 text-[14px]",
      "md:text-[16px]",
      "lg:text-[17px]",
      "xl:text-[18px]",
    );

    if (!trimmed) {
      return (
        <span className={rowCls} style={{ color }}>
          <span className={labelCls}>{label}</span>
          <BlogCardReadMoreArrow className={arrowCls} />
        </span>
      );
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return (
        <Link
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className={rowCls}
          style={{ color }}
        >
          <span className={labelCls}>{label}</span>
          <BlogCardReadMoreArrow className={arrowCls} />
        </Link>
      );
    }
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return (
      <Link href={path} className={rowCls} style={{ color }}>
        <span className={labelCls}>{label}</span>
        <BlogCardReadMoreArrow className={arrowCls} />
      </Link>
    );
  };

  const latestBlogCards = visiblePosts.map(
    (post: LatestBlogMeta, i: number) => (
      <article
        key={`${post.id ?? post.meta_title}-${startIndex + i}`}
        className={cn(
          "flex min-h-0  flex-col overflow-hidden rounded-lg border border-gray-3 bg-[#f8f9fa] transition-all duration-300 not-italic",
          "[font-family:var(--font-poppins),Poppins,sans-serif]",
          "shrink-0 tab:min-w-0 lg:min-w-0",
          "h-[375px] w-[307px] p-spacing-16",
          "tab:h-auto tab:w-full tab:min-h-[420px]",
          "md:min-h-[438px]",
          "lg:h-auto lg:min-h-[420px] lg:max-w-none",
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-spacing-12">
          <div
            className={cn(
              "relative mx-auto shrink-0 overflow-hidden rounded-lg",
              "h-[168px] w-[273px]",
              "tab:mx-0 tab:h-[138px] tab:w-full",
              "lg:aspect-[296/138] lg:h-auto lg:w-full",
            )}
          >
            {post.meta_cover_image ? (
              <Image
                src={post.meta_cover_image}
                alt={post.meta_cover_alt || ""}
                fill
                className="object-cover"
                sizes="(max-width: 767px) 273px, (max-width: 1429px) 273px, min(574px, 33vw)"
              />
            ) : (
              <div className="absolute inset-0 bg-neutral-200" aria-hidden />
            )}
          </div>

          <div
            className={cn(
              "flex min-h-0 w-full flex-1 flex-col gap-spacing-8",
              "tab:gap-spacing-24",
            )}
          >
            <div className="flex w-full flex-col gap-spacing-8">
              {post.meta_date_display ? (
                <div className="flex items-center gap-2">
                  <Calendar
                    className="h-[21px] w-[21px] shrink-0 tab:size-7 md:size-[30px] lg:size-8 xl:size-[34px]"
                    strokeWidth={1.75}
                    aria-hidden
                    style={{ color: BLOG_CARD_DATE_ICON }}
                  />
                  <span
                    className="whitespace-nowrap text-[13px] font-semibold leading-tight tracking-0 tab:text-[14px] md:text-[16px] lg:text-[17px] xl:text-[18px]"
                    style={{ color: BLOG_CARD_DATE_TEXT }}
                  >
                    {post.meta_date_display}
                  </span>
                </div>
              ) : null}
              <h3
                className={cn(
                  "line-clamp-3 w-full text-left text-[18px] font-bold leading-normal tracking-0",
                  "tab:text-[18px] tab:font-semibold",
                  "lg:text-[24px] lg:font-semibold",
                  "xl:text-[26px] xl:font-bold",
                )}
                style={{
                  color: "#101112",
                }}
              >
                <Link href={String(post.meta_url ?? "")} target="_blank">
                  {post.meta_title || ""}
                </Link>
              </h3>
              <p
                className={cn(
                  /* line-clamp sets height from line count; fixed tab/md h-* was ~2 lines and hid the 3rd */
                  "xs:hidden line-clamp-4 tab:line-clamp-3 w-full text-[14px] font-normal leading-relaxed",
                  "md:text-[16px]",
                  "lg:text-[17px]",
                  "xl:text-[18px]",
                )}
                style={{
                  color: "#101112",
                }}
              >
                {post.meta_description || ""}
              </p>
            </div>
            <div className="mt-auto flex w-full shrink-0 flex-col items-start">
              {readMoreRow(String(post.meta_url ?? ""))}
            </div>
          </div>
        </div>
      </article>
    ),
  );

  return (
    <section
      ref={ref}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-6 px-spacing-8 pt-[42px] pb-8 sm:gap-6 sm:px-[36px] sm:pb-[42px] md:px-[44px] md:pb-[52px] lg:px-[52px] lg:pb-[60px] xl:px-[64px] xl:pb-[72px]",
      )}
      style={{ backgroundColor: section_background_color || "#FFFFFF" }}
    >
      <div className="mx-auto flex w-full max-w-[1750px] flex-col gap-6">
        <h2
          className={cn(
            "xs:text-center sm:text-left text-[26px] font-semibold leading-tight md:text-[28px] lg:text-[34px] xl:text-[42px]",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>

        {isMobileLayout ? (
          <div className="flex w-full flex-col items-center gap-y-[24px]">
            {latestBlogCards}
          </div>
        ) : (
          <div className="mx-auto w-full tab:grid tab:grid-cols-3 tab:justify-items-stretch tab:gap-x-[14px] tab:gap-y-[24px] md:gap-x-[18px] md:gap-y-[28px] lg:gap-x-[14px] lg:gap-y-[24px]">
            {latestBlogCards}
          </div>
        )}

        {showNav ? (
          <div className="mt-10 flex justify-center gap-6">
            <button
              type="button"
              aria-label="Previous blogs"
              disabled={!canPrev}
              onClick={goPrev}
              className={cn(
                "flex size-12 shrink-0 items-center justify-center rounded-full transition-opacity",
                !canPrev && "cursor-not-allowed opacity-70"
              )}
              style={{ backgroundColor: prevBg, color: nav_icon_color }}
            >
              <ChevronLeft className="size-7" strokeWidth={2.25} aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Next blogs"
              disabled={!canNext}
              onClick={goNext}
              className={cn(
                "flex size-12 shrink-0 items-center justify-center rounded-full transition-opacity",
                !canNext && "cursor-not-allowed opacity-70"
              )}
              style={{ backgroundColor: nextBg, color: nav_icon_color }}
            >
              <ChevronRight className="size-7" strokeWidth={2.25} aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
